import React from 'react'

// Minimal, dependency-free markdown renderer for the assistant's replies.
// Supports the subset Ask Soroby actually emits: **bold**, *italic*, `code`,
// headings (#..######), ordered/unordered lists, and paragraphs / line breaks.
// Renders real React nodes (no dangerouslySetInnerHTML) so there's no XSS risk.

// Parse inline formatting within a single line of text.
const renderInline = (text, keyPrefix) => {
    const nodes = []
    const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g
    let lastIndex = 0
    let match
    let i = 0
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index))
        }
        const key = `${keyPrefix}-i${i++}`
        if (match[2] !== undefined) nodes.push(<strong key={key}>{match[2]}</strong>)
        else if (match[3] !== undefined) nodes.push(<em key={key}>{match[3]}</em>)
        else if (match[4] !== undefined) nodes.push(<code key={key}>{match[4]}</code>)
        lastIndex = regex.lastIndex
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
    return nodes
}

const ORDERED = /^\s*\d+\.\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const HEADING = /^(#{1,6})\s+(.*)$/

const Markdown = ({ text = '' }) => {
    const lines = String(text).split('\n')
    const blocks = []
    let list = null // { ordered: bool, items: [] }
    let para = []   // buffer of consecutive text lines

    const flushPara = () => {
        if (para.length) {
            const key = `p-${blocks.length}`
            blocks.push(
                <p key={key} className="md-p">
                    {para.map((ln, idx) => (
                        <React.Fragment key={`${key}-l${idx}`}>
                            {idx > 0 && <br />}
                            {renderInline(ln, `${key}-l${idx}`)}
                        </React.Fragment>
                    ))}
                </p>
            )
            para = []
        }
    }

    const flushList = () => {
        if (list) {
            const key = `list-${blocks.length}`
            const Tag = list.ordered ? 'ol' : 'ul'
            blocks.push(
                <Tag key={key} className="md-list">
                    {list.items.map((item, idx) => (
                        <li key={`${key}-li${idx}`}>{renderInline(item, `${key}-li${idx}`)}</li>
                    ))}
                </Tag>
            )
            list = null
        }
    }

    lines.forEach((raw) => {
        const line = raw.replace(/\s+$/, '')

        if (line.trim() === '') {
            flushPara()
            flushList()
            return
        }

        const heading = line.match(HEADING)
        if (heading) {
            flushPara()
            flushList()
            const level = heading[1].length
            blocks.push(
                <div key={`h-${blocks.length}`} className={`md-h md-h${level}`}>
                    {renderInline(heading[2], `h-${blocks.length}`)}
                </div>
            )
            return
        }

        const ordered = line.match(ORDERED)
        const bullet = line.match(BULLET)
        if (ordered || bullet) {
            flushPara()
            const isOrdered = Boolean(ordered)
            if (!list || list.ordered !== isOrdered) {
                flushList()
                list = { ordered: isOrdered, items: [] }
            }
            list.items.push((ordered ? ordered[1] : bullet[1]))
            return
        }

        // Plain text line — part of a paragraph.
        flushList()
        para.push(line)
    })

    flushPara()
    flushList()

    return <>{blocks}</>
}

export default Markdown
